import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Wand2, Power, PowerOff, HelpCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const emptyForm = {
  question: '',
  answer: '',
  category: '',
  order: 0,
  is_active: true,
};

const FaqManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/faq/admin/all');
      setFaqs(res.data?.faqs || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const openNew = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, order: faqs.length });
    setShowDialog(true);
  };

  const openEdit = (f) => {
    setEditingId(f.id);
    setFormData({
      question: f.question || '',
      answer: f.answer || '',
      category: f.category || '',
      order: f.order || 0,
      is_active: !!f.is_active,
    });
    setShowDialog(true);
  };

  const handleAiSuggest = async () => {
    if (!formData.question.trim()) return toast.error('Bitte zuerst die Frage eintragen');
    setAiLoading(true);
    try {
      const res = await axiosInstance.post('/faq/admin/ai-suggest', {
        question: formData.question,
        context: formData.category || null,
      });
      if (res.data?.success && res.data?.answer) {
        setFormData((prev) => ({ ...prev, answer: res.data.answer }));
        toast.success('KI-Vorschlag eingefügt — nach Belieben anpassen');
      } else {
        toast.error(res.data?.error || 'KI konnte keinen Vorschlag erstellen');
      }
    } catch {
      toast.error('KI-Anfrage fehlgeschlagen');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      question: formData.question.trim(),
      answer: formData.answer.trim(),
      category: formData.category.trim() || null,
      order: Number(formData.order) || 0,
      is_active: formData.is_active,
    };
    if (!payload.question || !payload.answer) return toast.error('Frage und Antwort sind Pflichtfelder');
    try {
      if (editingId) {
        await axiosInstance.patch(`/faq/admin/${editingId}`, payload);
        toast.success('Aktualisiert');
      } else {
        await axiosInstance.post('/faq/admin', payload);
        toast.success('Erstellt');
      }
      setShowDialog(false);
      setFormData(emptyForm);
      fetchFaqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('FAQ wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/faq/admin/${id}`);
      toast.success('Gelöscht');
      fetchFaqs();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleToggle = async (f) => {
    try {
      await axiosInstance.patch(`/faq/admin/${f.id}`, { is_active: !f.is_active });
      fetchFaqs();
    } catch { toast.error('Fehler'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= faqs.length) return;
    const a = faqs[idx];
    const b = faqs[target];
    try {
      await Promise.all([
        axiosInstance.patch(`/faq/admin/${a.id}`, { order: b.order }),
        axiosInstance.patch(`/faq/admin/${b.id}`, { order: a.order }),
      ]);
      fetchFaqs();
    } catch { toast.error('Fehler beim Verschieben'); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><p>Lädt...</p></div>;

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 lg:py-8" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }} data-testid="faq-manager-page">
      <AdminPageHeader
        title="FAQ verwalten"
        subtitle={`${faqs.length} Einträge — mit KI-Texthilfe`}
        icon={HelpCircle}
      />

      <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
            <Plus className="h-4 w-4 text-[#60B060]" />
          </div>
          <h2 className="text-base font-semibold text-white">Aktionen</h2>
        </div>
        <Button
          onClick={openNew}
          className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none"
          data-testid="new-faq-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Neue FAQ
        </Button>
      </div>

      <div className="mb-3 p-2 px-3 flex items-center gap-2 mt-4" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
          <HelpCircle className="h-4 w-4 text-[#60A0D0]" />
        </div>
        <h2 className="text-base font-semibold text-white flex-1">Alle FAQs</h2>
        <span className="text-[11px] text-white/80 italic hidden sm:inline">
          Karte klicken zum Bearbeiten
        </span>
      </div>

      {faqs.length === 0 ? (
        <Card className="rounded-none border-[#C4A084]">
          <CardContent className="p-8 text-center text-[#8B7355]">
            Noch keine FAQs. Klicke „Neue FAQ" um zu starten.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {faqs.map((f, idx) => (
            <div
              key={f.id}
              className={`bg-white border ${f.is_active ? 'border-[#E8DCC8]' : 'border-dashed border-gray-300 opacity-60'} p-3 hover:shadow-md hover:border-[#5D4E3C] transition-all cursor-pointer group`}
              onClick={(e) => { if (!e.target.closest('button')) openEdit(f); }}
              data-testid={`faq-row-${f.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 flex flex-col items-center gap-0.5 pt-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); move(idx, -1); }}
                    className="text-[#8B7355] hover:text-[#5D4E3C] disabled:opacity-30 disabled:cursor-not-allowed"
                    disabled={idx === 0}
                    title="Nach oben"
                    data-testid={`faq-move-up-${f.id}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <span className="text-[10px] font-medium text-[#8B7355]">#{idx + 1}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); move(idx, 1); }}
                    className="text-[#8B7355] hover:text-[#5D4E3C] disabled:opacity-30 disabled:cursor-not-allowed"
                    disabled={idx === faqs.length - 1}
                    title="Nach unten"
                    data-testid={`faq-move-down-${f.id}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#5D4E3C] group-hover:underline">{f.question}</p>
                  <p className="text-xs text-[#8B7355] mt-1 line-clamp-2">{f.answer}</p>
                  {f.category && (
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-widest text-[#B0855C]">
                      {f.category}
                    </span>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleToggle(f); }}
                    variant="ghost"
                    size="sm"
                    className={f.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-emerald-600 hover:bg-emerald-50'}
                    title={f.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    data-testid={`faq-toggle-${f.id}`}
                  >
                    {f.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    title="Löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-none max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold mb-4 text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
                {editingId ? 'FAQ bearbeiten' : 'Neue FAQ'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Frage *</Label>
                  <Input
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    placeholder="Wie lange dauert der Versand?"
                    required
                    className="mt-1 rounded-none"
                    data-testid="faq-form-question"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-medium">Antwort *</Label>
                    <Button
                      type="button"
                      onClick={handleAiSuggest}
                      disabled={aiLoading || !formData.question.trim()}
                      className="rounded-none bg-[#F5EDE4] text-[#5D4E3C] hover:bg-[#EFE5D7] border border-[#C4A084] h-7 px-2 text-xs"
                      data-testid="faq-ai-suggest-btn"
                    >
                      <Wand2 className={`w-3.5 h-3.5 mr-1 ${aiLoading ? 'animate-spin' : ''}`} />
                      {aiLoading ? 'KI schreibt…' : 'KI-Vorschlag'}
                    </Button>
                  </div>
                  <Textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    rows={5}
                    placeholder="Deine Antwort — oder erst KI-Vorschlag holen und dann anpassen"
                    required
                    className="rounded-none"
                    data-testid="faq-form-answer"
                  />
                  <p className="text-[10px] text-[#8B7355] mt-0.5">
                    Tipp: Erst Frage eintragen, dann „KI-Vorschlag" klicken. Text kannst du danach frei anpassen.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Kategorie</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="z. B. Versand · optional"
                      className="mt-1 rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Reihenfolge</Label>
                    <Input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      className="mt-1 rounded-none"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5D4E3C]">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Aktiv — öffentlich sichtbar
                </label>
                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-3">
                  <Button
                    type="button"
                    onClick={() => { setShowDialog(false); setFormData(emptyForm); setEditingId(null); }}
                    className="rounded-none flex-1 sm:flex-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-none flex-1 sm:flex-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                    data-testid="faq-form-submit"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Aktualisieren' : 'Erstellen'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqManager;
